import { useState, useEffect } from "react";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { 
  Plus, Pencil, Trash2, BookOpen, Users, Eye, EyeOff, 
  GraduationCap, Video, FileText, Download, PenTool, Lightbulb,
  ChevronRight, Settings
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTENT_TYPES = [
  { id: "video", label: "Video", icon: Video },
  { id: "document", label: "Dokument", icon: FileText },
  { id: "exercise", label: "Übung", icon: PenTool },
  { id: "solution", label: "Lösung", icon: Lightbulb },
  { id: "download", label: "Download", icon: Download }
];

const CourseManagement = () => {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  
  // Dialogs
  const [courseDialog, setCourseDialog] = useState(false);
  const [moduleDialog, setModuleDialog] = useState(false);
  const [contentDialog, setContentDialog] = useState(false);
  const [accessDialog, setAccessDialog] = useState(false);
  
  // Form states
  const [courseForm, setCourseForm] = useState({ title: "", description: "", category: "NTG" });
  const [moduleForm, setModuleForm] = useState({ title: "", description: "" });
  const [contentForm, setContentForm] = useState({ title: "", content_type: "video", content_url: "", content_text: "", duration_minutes: null });
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [coursesRes, categoriesRes, participantsRes] = await Promise.all([
        axios.get(`${API}/courses/all`),
        axios.get(`${API}/courses/categories`),
        axios.get(`${API}/users/by-role/participant`)
      ]);
      setCourses(coursesRes.data);
      setCategories(categoriesRes.data);
      setParticipants(participantsRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const loadCourseDetails = async (courseId) => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`);
      setSelectedCourse(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden des Kurses");
    }
  };

  // Course CRUD
  const handleCreateCourse = async () => {
    try {
      const response = await axios.post(`${API}/courses`, courseForm);
      toast.success("Kurs erstellt");
      setCourseDialog(false);
      setCourseForm({ title: "", description: "", category: "NTG" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    }
  };

  const handleUpdateCourse = async () => {
    try {
      await axios.put(`${API}/courses/${editingCourse.id}`, courseForm);
      toast.success("Kurs aktualisiert");
      setCourseDialog(false);
      setEditingCourse(null);
      setCourseForm({ title: "", description: "", category: "NTG" });
      loadData();
      if (selectedCourse?.id === editingCourse.id) {
        loadCourseDetails(editingCourse.id);
      }
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm("Kurs wirklich löschen? Alle Inhalte werden gelöscht.")) return;
    try {
      await axios.delete(`${API}/courses/${courseId}`);
      toast.success("Kurs gelöscht");
      loadData();
      if (selectedCourse?.id === courseId) {
        setSelectedCourse(null);
      }
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const toggleCourseVisibility = async (course) => {
    try {
      await axios.put(`${API}/courses/${course.id}`, { is_visible: !course.is_visible });
      toast.success(course.is_visible ? "Kurs ausgeblendet" : "Kurs eingeblendet");
      loadData();
    } catch (error) {
      toast.error("Fehler");
    }
  };

  // Module CRUD
  const handleCreateModule = async () => {
    if (!selectedCourse) return;
    try {
      await axios.post(`${API}/courses/${selectedCourse.id}/modules`, null, {
        params: { title: moduleForm.title, description: moduleForm.description }
      });
      toast.success("Modul erstellt");
      setModuleDialog(false);
      setModuleForm({ title: "", description: "" });
      loadCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm("Modul löschen?")) return;
    try {
      await axios.delete(`${API}/courses/modules/${moduleId}`);
      toast.success("Modul gelöscht");
      loadCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  // Content CRUD
  const handleCreateContent = async () => {
    if (!selectedModuleId) return;
    try {
      await axios.post(`${API}/courses/modules/${selectedModuleId}/content`, null, {
        params: {
          title: contentForm.title,
          content_type: contentForm.content_type,
          content_url: contentForm.content_url || undefined,
          content_text: contentForm.content_text || undefined,
          duration_minutes: contentForm.duration_minutes || undefined
        }
      });
      toast.success("Inhalt erstellt");
      setContentDialog(false);
      setContentForm({ title: "", content_type: "video", content_url: "", content_text: "", duration_minutes: null });
      setSelectedModuleId(null);
      loadCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm("Inhalt löschen?")) return;
    try {
      await axios.delete(`${API}/courses/content/${contentId}`);
      toast.success("Inhalt gelöscht");
      loadCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  // Access Management
  const [courseAccess, setCourseAccess] = useState([]);
  
  const loadCourseAccess = async (courseId) => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}/access`);
      setCourseAccess(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const grantAccess = async (userId) => {
    if (!selectedCourse) return;
    try {
      await axios.post(`${API}/courses/${selectedCourse.id}/access`, null, {
        params: { user_id: userId }
      });
      toast.success("Zugang gewährt");
      loadCourseAccess(selectedCourse.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler");
    }
  };

  const revokeAccess = async (userId) => {
    if (!selectedCourse) return;
    try {
      await axios.delete(`${API}/courses/${selectedCourse.id}/access/${userId}`);
      toast.success("Zugang entzogen");
      loadCourseAccess(selectedCourse.id);
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const openAccessDialog = (course) => {
    setSelectedCourse(course);
    loadCourseAccess(course.id);
    setAccessDialog(true);
  };

  const openEditCourse = (course) => {
    setEditingCourse(course);
    setCourseForm({ title: course.title, description: course.description || "", category: course.category });
    setCourseDialog(true);
  };

  const accessUserIds = courseAccess.map(a => a.user_id);

  return (
    <AdminLayout title="Kursverwaltung">
      <div className="space-y-6" data-testid="course-management">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <p className="text-muted-foreground">
            Verwalten Sie Kurse, Module und Inhalte für die Lernplattform.
          </p>
          <Button onClick={() => { setEditingCourse(null); setCourseForm({ title: "", description: "", category: "NTG" }); setCourseDialog(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Neuer Kurs
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Course List */}
          <Card className="bg-card border-border lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Kurse ({courses.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
                {courses.map(course => (
                  <div 
                    key={course.id}
                    className={`p-3 cursor-pointer hover:bg-secondary/30 transition-colors ${
                      selectedCourse?.id === course.id ? "bg-primary/10 border-l-2 border-primary" : ""
                    }`}
                    onClick={() => loadCourseDetails(course.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{course.title}</span>
                          {!course.is_visible && <EyeOff className="h-3 w-3 text-muted-foreground" />}
                        </div>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {course.category}
                        </Badge>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
                {courses.length === 0 && (
                  <p className="p-4 text-center text-muted-foreground text-sm">
                    Keine Kurse vorhanden
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Course Details */}
          <Card className="bg-card border-border lg:col-span-2">
            {selectedCourse ? (
              <>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{selectedCourse.title}</CardTitle>
                      <CardDescription>{selectedCourse.category}</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openAccessDialog(selectedCourse)}>
                        <Users className="h-4 w-4 mr-1" />
                        Zugang
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => toggleCourseVisibility(selectedCourse)}>
                        {selectedCourse.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEditCourse(selectedCourse)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteCourse(selectedCourse.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium">Module</h3>
                      <Button size="sm" onClick={() => setModuleDialog(true)}>
                        <Plus className="h-4 w-4 mr-1" />
                        Modul
                      </Button>
                    </div>
                    
                    {selectedCourse.modules?.length > 0 ? (
                      <div className="space-y-4">
                        {selectedCourse.modules.map((module, idx) => (
                          <Card key={module.id} className="bg-secondary/20 border-border">
                            <CardHeader className="py-3">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-sm">{idx + 1}. {module.title}</CardTitle>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => { setSelectedModuleId(module.id); setContentDialog(true); }}>
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteModule(module.id)}>
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                              {module.content?.length > 0 ? (
                                <div className="space-y-1">
                                  {module.content.map((content, cIdx) => {
                                    const TypeIcon = CONTENT_TYPES.find(t => t.id === content.content_type)?.icon || FileText;
                                    return (
                                      <div key={content.id} className="flex items-center justify-between p-2 rounded-md bg-background/50 text-sm">
                                        <div className="flex items-center gap-2">
                                          <TypeIcon className="h-4 w-4 text-muted-foreground" />
                                          <span>{content.title}</span>
                                          {content.duration_minutes && (
                                            <span className="text-xs text-muted-foreground">({content.duration_minutes}m)</span>
                                          )}
                                        </div>
                                        <Button variant="ghost" size="sm" onClick={() => handleDeleteContent(content.id)}>
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground">Keine Inhalte</p>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm py-8 text-center">
                        Noch keine Module. Erstellen Sie das erste Modul.
                      </p>
                    )}
                  </div>
                </CardContent>
              </>
            ) : (
              <CardContent className="py-12 text-center text-muted-foreground">
                <GraduationCap className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Wählen Sie einen Kurs aus der Liste</p>
              </CardContent>
            )}
          </Card>
        </div>

        {/* Course Dialog */}
        <Dialog open={courseDialog} onOpenChange={setCourseDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>{editingCourse ? "Kurs bearbeiten" : "Neuer Kurs"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Titel</Label>
                <Input
                  value={courseForm.title}
                  onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                  placeholder="Kursname"
                />
              </div>
              <div className="space-y-2">
                <Label>Kategorie</Label>
                <Select value={courseForm.category} onValueChange={(v) => setCourseForm({ ...courseForm, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Beschreibung</Label>
                <Textarea
                  value={courseForm.description}
                  onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                  placeholder="Kursbeschreibung..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCourseDialog(false)}>Abbrechen</Button>
              <Button onClick={editingCourse ? handleUpdateCourse : handleCreateCourse}>
                {editingCourse ? "Speichern" : "Erstellen"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Module Dialog */}
        <Dialog open={moduleDialog} onOpenChange={setModuleDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Neues Modul</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Titel</Label>
                <Input
                  value={moduleForm.title}
                  onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                  placeholder="Modulname"
                />
              </div>
              <div className="space-y-2">
                <Label>Beschreibung (optional)</Label>
                <Textarea
                  value={moduleForm.description}
                  onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setModuleDialog(false)}>Abbrechen</Button>
              <Button onClick={handleCreateModule}>Erstellen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Content Dialog */}
        <Dialog open={contentDialog} onOpenChange={setContentDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Neuer Inhalt</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Titel</Label>
                <Input
                  value={contentForm.title}
                  onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })}
                  placeholder="Inhaltstitel"
                />
              </div>
              <div className="space-y-2">
                <Label>Typ</Label>
                <Select value={contentForm.content_type} onValueChange={(v) => setContentForm({ ...contentForm, content_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map(type => (
                      <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(contentForm.content_type === "video" || contentForm.content_type === "download") && (
                <div className="space-y-2">
                  <Label>URL</Label>
                  <Input
                    value={contentForm.content_url}
                    onChange={(e) => setContentForm({ ...contentForm, content_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              )}
              {contentForm.content_type === "video" && (
                <div className="space-y-2">
                  <Label>Dauer (Minuten)</Label>
                  <Input
                    type="number"
                    value={contentForm.duration_minutes || ""}
                    onChange={(e) => setContentForm({ ...contentForm, duration_minutes: parseInt(e.target.value) || null })}
                    placeholder="15"
                  />
                </div>
              )}
              {(contentForm.content_type === "document" || contentForm.content_type === "exercise" || contentForm.content_type === "solution") && (
                <>
                  <div className="space-y-2">
                    <Label>Text/Inhalt</Label>
                    <Textarea
                      value={contentForm.content_text}
                      onChange={(e) => setContentForm({ ...contentForm, content_text: e.target.value })}
                      rows={5}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Dokument-URL (optional)</Label>
                    <Input
                      value={contentForm.content_url}
                      onChange={(e) => setContentForm({ ...contentForm, content_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setContentDialog(false)}>Abbrechen</Button>
              <Button onClick={handleCreateContent}>Erstellen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Access Dialog */}
        <Dialog open={accessDialog} onOpenChange={setAccessDialog}>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader>
              <DialogTitle>Kurszugang verwalten</DialogTitle>
              <DialogDescription>
                Wählen Sie Teilnehmer, die Zugang zu "{selectedCourse?.title}" erhalten sollen.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Teilnehmer</TableHead>
                    <TableHead>E-Mail</TableHead>
                    <TableHead className="text-right">Zugang</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {participants.map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{p.name}</TableCell>
                      <TableCell className="text-muted-foreground">{p.email}</TableCell>
                      <TableCell className="text-right">
                        {accessUserIds.includes(p.id) ? (
                          <Button variant="destructive" size="sm" onClick={() => revokeAccess(p.id)}>
                            Entziehen
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => grantAccess(p.id)}>
                            Gewähren
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <DialogFooter>
              <Button onClick={() => setAccessDialog(false)}>Schließen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default CourseManagement;
